import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

// Public one-click extend page — the token in the URL is the auth.
// Reached from the retention warning email's magic link.
export default function ExtendMagic() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const days = Math.max(1, Math.min(3650, parseInt(params.get("days") || "30", 10)));
  const [state, setState] = useState("loading"); // loading | success | already | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setState("error");
      setError("Missing token in link.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/extend-magic", { token, days });
        setResult(data);
        setState(data.already_consumed ? "already" : "success");
      } catch (err) {
        setError(formatApiError(err));
        setState("error");
      }
    })();
  }, [token, days]);

  return (
    <div className="max-w-2xl mx-auto px-6 mt-24 pb-20 text-center" data-testid="extend-magic-page">
      {state === "loading" && (
        <div data-testid="extend-magic-loading">
          <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-4">One moment</p>
          <h1 className="font-display text-4xl uppercase mb-6">Extending your photos…</h1>
          <div className="w-10 h-10 border-2 border-white/20 border-t-[var(--pm-orange)] rounded-full animate-spin mx-auto" />
        </div>
      )}

      {state === "success" && result && (
        <div data-testid="extend-magic-success">
          <p className="text-xs uppercase tracking-[0.3em] text-green-400 font-semibold mb-4">Done</p>
          <h1 className="font-display text-4xl uppercase mb-6">
            {result.extended} photo{result.extended === 1 ? "" : "s"} extended by {result.days_added} days
          </h1>
          <p className="text-white/70 font-condensed text-lg mb-8">
            {result.expires_at
              ? `They're now safe until ${new Date(result.expires_at).toLocaleDateString()}.`
              : "You're all set."}
          </p>
          <Link to="/studio" className="pm-btn inline-block" data-testid="extend-magic-cta-dashboard">
            Go to dashboard
          </Link>
        </div>
      )}

      {state === "already" && result && (
        <div data-testid="extend-magic-already">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 font-semibold mb-4">Already extended</p>
          <h1 className="font-display text-4xl uppercase mb-6">
            You&apos;ve already used this link
          </h1>
          <p className="text-white/70 font-condensed text-lg mb-8">
            {result.extended} photo{result.extended === 1 ? "" : "s"} were extended earlier
            {result.expires_at ? ` and are safe until ${new Date(result.expires_at).toLocaleDateString()}.` : "."}
          </p>
          <Link to="/studio" className="pm-btn inline-block" data-testid="extend-magic-cta-dashboard">
            Go to dashboard
          </Link>
        </div>
      )}

      {state === "error" && (
        <div data-testid="extend-magic-error">
          <p className="text-xs uppercase tracking-[0.3em] text-red-400 font-semibold mb-4">Link couldn&apos;t be used</p>
          <h1 className="font-display text-4xl uppercase mb-6">Something went wrong</h1>
          <p className="text-white/70 font-condensed text-lg mb-8">{error}</p>
          <Link to="/studio" className="pm-btn inline-block" data-testid="extend-magic-cta-login">
            Log in to extend manually
          </Link>
        </div>
      )}
    </div>
  );
}
